/**
 * User settings (SPEC §18 "User" scope) as the first synced document: `settings/main`.
 * Field-mergeable, so two devices editing different keys never clobber each other.
 */
import { create } from "zustand";
import type { FormFactorOverride } from "./layout.ts";
import { DEFAULT_TIME_ZONE_SETTING, type TimeZoneSetting } from "./time/zone.ts";
import { DEFAULT_TIMEFRAME_FAVORITES } from "./bars/timeframe.ts";
import { DEFAULT_QTY_FAVORITES } from "./trading/qtyFavorites.ts";
import { DocSync } from "./sync/client.ts";
import type { Doc } from "./sync/docs.ts";
import type { IndicatorTemplate } from "./indicators/types.ts";
import {
  effectiveVisualStyle,
  visualStylePatch,
  type ChartToolStrip,
  type TopBarClock,
  type VisualStyle,
  type ColorScheme,
} from "./visualStyle.ts";

export * from "./visualStyle.ts";

export type Theme = "dark" | "light";
/** Top-bar account figures: Tradesea chips, or the compact "Paper · BAL · OPEN · DAY" strip. */
export type AccountStrip = "chips" | "compact";
export type SidePalette = "greenRed" | "blueRed" | "colorBlindSafe";
export type TimeDisplay = "local" | "ct";
/** DOM ladder P&L column units (owner 2026-09-27): header click toggles. */
export type DomPnlUnit = "dollars" | "ticks";
/** Chart trade-line P&L (owner 2026-09-28): cash by default, or points / ticks per contract. */
export type ChartPnlUnit = "cash" | "points" | "ticks";
/**
 * First-run setup (layout preset → style → scheme, owner 2026-09-30 UI-REVAMP §8): "pending" only
 * in a settings document this client created for a new user (`NEW_USER_SETTINGS`); finishing or
 * skipping it stores "completed" / "dismissed", so no device offers it again. Documents saved
 * before the setup existed have no value and read as "not_offered": existing users never see it.
 */
export type FirstRunSetup = "pending" | "completed" | "dismissed" | "not_offered";

/** A stored chart P&L unit, or the default (cash) for anything unknown. */
export function chartPnlUnitOf(v: unknown): ChartPnlUnit {
  return v === "points" || v === "ticks" ? v : "cash";
}

/** Exit All asks unless the user explicitly opted out (older/invalid documents still ask). */
export function confirmExitAllOf(v: unknown): boolean {
  return v !== false;
}

export type Settings = {
  theme: Theme;
  /** Independent palette; legacy preserves the saved appearance without a migration write. */
  color_scheme: ColorScheme;
  /** Optional six-digit accent; scheme application keeps text contrast readable. */
  accent_color: string | null;
  /**
   * Calm (Settings → Appearance): turns a mood theme's flair motion off. Missing in older
   * documents = off; flair exists only with a mood scheme, so base schemes never change.
   * Read through `calmOf`.
   */
  appearance_calm: boolean;
  /** Visual style (see `VisualStyle`); read through `effectiveVisualStyle`. */
  visual_style: VisualStyle;
  /**
   * The user picked `visual_style` themselves (Settings → Appearance or the palette). Documents
   * created before Tradovate became the default stored "tradesea" without a choice: those users get
   * Tradovate; a stored "tradesea" with this flag is kept.
   */
  visual_style_chosen: boolean;
  /** Chart drawing tools (see `ChartToolStrip`); missing = "auto". */
  chart_tool_strip: ChartToolStrip;
  /** Top-bar clock (see `TopBarClock`); missing = "auto". */
  topbar_clock: TopBarClock;
  /** Top-bar account figures layout; missing in older documents = "chips". */
  account_strip: AccountStrip;
  sidePalette: SidePalette;
  formFactor: FormFactorOverride;
  timeDisplay: TimeDisplay;
  sounds: boolean;
  oneClick: boolean;
  /**
   * The one-click explanation (SPEC §9.8 "enabling it shows one explanation dialog") was read and
   * accepted once; afterwards the DOM / order pad toggle switches one-click with a single click
   * (owner 2026-09-28 "dom needs to easily be able to remove the confirmation").
   */
  one_click_explained: boolean;
  /**
   * Large-order confirmation (OB4, SPEC §9.8, owner 2026-09-30 Q-L2): an order above the
   * account's "Confirm orders above" threshold asks first, even with one-click on. The popover's
   * "Don't ask again for large orders" (ticked and confirmed) turns it off; Settings → "Confirm
   * large orders" turns it back on. Default on. Read through `confirmLargeOrdersOf`.
   */
  confirm_large_orders: boolean;
  /** Owner 2026-10-02: Exit All's remembered confirmation choice; independent of one-click. */
  confirm_exit_all: boolean;
  /** DOM auto-centre on the last price (SPEC §13.1); scrolling pauses it for 5 s. Default on. */
  dom_auto_center: boolean;
  /**
   * The symbol dropdown's Recent section (DOM, order pad, chart panes; owner 2026-09-28): the
   * symbols the user picked, with use counts and last use, newest first. Untrusted: read through
   * `sanitizeMru` (symbolSwitch.ts).
   */
  symbol_mru: unknown;
  privacy: boolean;
  /**
   * Display time zone for every clock and timestamp (owner 2026-09-27, extends SPEC C10):
   * "local" (browser zone, default), "exchange" (America/Chicago) or an IANA id. Resolve with
   * `resolveTimeZone` (time/zone.ts). Supersedes `timeDisplay`, which is kept for old documents.
   */
  display_time_zone: TimeZoneSetting;
  /** Most recent zone picks, newest first (picker shortcuts). */
  recent_time_zones: string[];
  /** DOM ladder estimated-P/L column: dollars (default) or ticks. */
  dom_pnl_unit: DomPnlUnit;
  /** Chart trade lines (position / order P&L if hit): cash (default), points or ticks. */
  chart_pnl_unit: ChartPnlUnit;
  /**
   * Chart timeframes pinned to the chart header (canonical strings, `bars/timeframe.ts`: "1m",
   * "1D", "1mo", "500t", "8r" …). Read through `sanitizeFavorites`.
   */
  chart_tf_favorites: string[];
  /**
   * Favourite order quantities (1–10; owner 2026-10-04): the DOM quantity chips and the chart order
   * bar's quick quantities. Missing in older documents = the defaults (1, 2, 3, 5, 10). Read
   * through `sanitizeQtyFavorites` (trading/qtyFavorites.ts).
   */
  qty_favorites: number[];
  /**
   * The user's main account (owner 2026-09-27, Tradovate-style "main account only / all
   * accounts"): marked in the top-bar account menu, used by "Main account only" views and as the
   * account per-account surfaces act on while "All accounts" is selected. null = first account.
   */
  main_account_id: string | null;
  /**
   * Saved table column templates (owner 2026-09-27 "a table template"), user-level so every
   * device has them: a `TableTemplatesDoc` (tables/templates.ts). Read through `tableTemplatesOf`.
   */
  table_templates: unknown;
  /** Indicators dialog favourites (registry ids, ADR-0018). Read through `sanitizeIndicatorFavorites`. */
  indicator_favorites: string[];
  /**
   * Saved indicator templates (TradingView "Save indicator template"): name + the indicators/panes
   * state, symbol- and timeframe-agnostic. Read through `sanitizeIndicatorTemplates`.
   */
  indicator_templates: IndicatorTemplate[];
  /**
   * The user's Pine scripts ("My scripts", SPEC-ADDENDA A6): a `UserScriptsDoc` (pine/scripts.ts),
   * source + licence + the last saves. Read through `sanitizeUserScripts`.
   */
  pine_scripts: unknown;
  /**
   * Window-wide top toolbar (owner 2026-09-28, Q-G3; both visual styles): symbol, timeframes,
   * Indicators, layers, Orders, Positions, Flatten, Alert and workspace actions for the active
   * chart. While it shows, chart headers keep only a compact timeframe menu. Default on.
   */
  top_toolbar: boolean;
  /**
   * Chart settings "Save as default" (TradingView template menu, ADR-0026): what a chart pane
   * without its own settings shows, on every device. null = the factory settings. Untrusted: read
   * through `sanitizeChartSettings` (@helm/chart).
   */
  chart_settings_default: unknown;
  /**
   * Saved chart templates (TradingView "Save chart template", owner 2026-09-28): the whole look of
   * a chart pane — style / colours / scale, indicators, overlays, compares, optionally symbol and
   * interval — plus the default for new charts. A `ChartTemplatesDoc` (chartTemplates.ts); read
   * through `sanitizeChartTemplates`. null = none saved.
   */
  chart_templates: unknown;
  /** First-run setup state (see `FirstRunSetup`); read through `firstRunSetupPending`. */
  first_run_setup: FirstRunSetup;
  [key: string]: unknown;
};

export const DEFAULT_SETTINGS: Settings = {
  theme: "dark",
  color_scheme: "legacy",
  accent_color: null,
  appearance_calm: false,
  visual_style: "tradovate",
  visual_style_chosen: false,
  chart_tool_strip: "auto",
  topbar_clock: "auto",
  account_strip: "chips",
  sidePalette: "greenRed",
  formFactor: "auto",
  timeDisplay: "local",
  sounds: true,
  oneClick: false, // SPEC §9.8: off by default
  one_click_explained: false,
  confirm_large_orders: true,
  confirm_exit_all: true,
  dom_auto_center: true,
  symbol_mru: [],
  privacy: false,
  display_time_zone: DEFAULT_TIME_ZONE_SETTING,
  recent_time_zones: [],
  dom_pnl_unit: "dollars",
  chart_pnl_unit: "cash",
  chart_tf_favorites: [...DEFAULT_TIMEFRAME_FAVORITES],
  qty_favorites: [...DEFAULT_QTY_FAVORITES],
  main_account_id: null,
  table_templates: null,
  indicator_favorites: ["ema", "vwap", "rsi"],
  indicator_templates: [],
  pine_scripts: null,
  top_toolbar: true,
  chart_settings_default: null,
  chart_templates: null,
  first_run_setup: "not_offered",
};

/**
 * The settings document a new user starts with (owner 2026-09-30 Q-UI1/Q-UI2: Pro style, Midnight
 * scheme), written in full by `SettingsSync.load` only when the server has no settings document.
 * `DEFAULT_SETTINGS` stays the reading of absent fields: a document saved before this change (with
 * or without its appearance keys) keeps the look it had, and this never migrates one.
 */
export const NEW_USER_SETTINGS: Settings = {
  ...DEFAULT_SETTINGS,
  visual_style: "pro",
  color_scheme: "midnight",
  first_run_setup: "pending",
};

/** Offer the first-run setup: only a new user's document that has not finished or skipped it. */
export function firstRunSetupPending(s: { readonly first_run_setup?: unknown }): boolean {
  return s.first_run_setup === "pending";
}

/** Settings patch for finishing the first-run setup with a style and scheme (the style is chosen). */
export function firstRunCompletePatch(
  style: VisualStyle,
  scheme: ColorScheme,
): Pick<Settings, "visual_style" | "visual_style_chosen" | "color_scheme" | "first_run_setup"> {
  return { ...visualStylePatch(style), color_scheme: scheme, first_run_setup: "completed" };
}

/** Settings patch for skipping the first-run setup: the appearance stays as it is. */
export function firstRunDismissPatch(): Pick<Settings, "first_run_setup"> {
  return { first_run_setup: "dismissed" };
}

const SIDE_PALETTES: Record<SidePalette, { buy: string; sell: string }> = {
  greenRed: { buy: "#1FA37A", sell: "#E5484D" },
  blueRed: { buy: "#2F7CF6", sell: "#E5484D" },
  colorBlindSafe: { buy: "#1E88E5", sell: "#FF8F00" },
};

/** Apply the visual settings to the document (theme attribute, side colours). */
export function applySettingsToDocument(s: Settings, doc: Document = document): void {
  doc.documentElement.dataset["theme"] = s.theme;
  doc.documentElement.dataset["style"] = effectiveVisualStyle(s);
  const p = SIDE_PALETTES[s.sidePalette];
  doc.documentElement.style.setProperty("--buy", p.buy);
  doc.documentElement.style.setProperty("--sell", p.sell);
  doc.documentElement.style.setProperty("--buy-weak", hexToRgba(p.buy, 0.16));
  doc.documentElement.style.setProperty("--sell-weak", hexToRgba(p.sell, 0.16));
}

function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${String((n >> 16) & 255)},${String((n >> 8) & 255)},${String(n & 255)},${String(alpha)})`;
}

export type SettingsState = {
  settings: Settings;
  version: number;
  loaded: boolean;
  syncStatus: "idle" | "saving" | "error";
  lastError: string | null;
};

export const useSettings = create<SettingsState>(() => ({
  settings: DEFAULT_SETTINGS,
  version: 0,
  loaded: false,
  syncStatus: "idle",
  lastError: null,
}));

/** Coordinates the store with the server document. One instance per window. */
export class SettingsSync {
  private base: Settings = DEFAULT_SETTINGS;
  private readonly docs: DocSync;
  private readonly apply: (s: Settings) => void;
  /** Edits not yet sent (the next save sends them). */
  private pending: Partial<Settings> = {};
  /** Intent awaiting its save result, below any newer pending edits in the visible settings. */
  private active: Partial<Settings> = {};
  /** The save in flight, if any: the next one starts after it. */
  private queue: Promise<void> = Promise.resolve();

  constructor(
    docs: DocSync = new DocSync(),
    apply: (s: Settings) => void = (s) => {
      applySettingsToDocument(s);
    },
  ) {
    this.docs = docs;
    this.apply = apply;
  }

  /**
   * Load from the server and apply. With no settings document (a new user) it creates one from
   * `NEW_USER_SETTINGS`; base and local are the same body, so on a creation conflict (another
   * device created it first) that device's document wins field for field.
   */
  async load(): Promise<void> {
    const startedVersion = useSettings.getState().version;
    const doc = await this.docs.load<Settings>("settings", "main");
    if (doc) {
      this.adopt(doc);
    } else {
      // A delayed 404 can predate a document already accepted from a refresh or save.
      const current = useSettings.getState();
      if (current.loaded && current.version > startedVersion) return;
      const r = await this.docs.save("settings", "main", NEW_USER_SETTINGS, 0, NEW_USER_SETTINGS);
      if (r.kind === "saved") this.adopt(r.doc);
      else this.adopt(r.server);
    }
  }

  /** Called when a `DocChanged` for settings arrives from another device. */
  async refresh(version: number): Promise<void> {
    if (version <= useSettings.getState().version) return;
    const doc = await this.docs.load<Settings>("settings", "main");
    if (doc) this.adopt(doc);
  }

  /**
   * Optimistic local update, then save with If-Match; merges on conflict. Saves go one at a time
   * (each from the version the previous one produced), and edits made while one is in flight stay
   * on screen and go in the next: two quick edits (one-click off → on) used to race, the second
   * saved from the stale version and the field merge took the first one's value back.
   */
  async update(patch: Partial<Settings>): Promise<void> {
    this.pending = { ...this.pending, ...patch };
    const local: Settings = { ...useSettings.getState().settings, ...patch };
    useSettings.setState({ settings: local, syncStatus: "saving", lastError: null });
    this.apply(local);
    const run = this.queue.then(() => this.flush());
    this.queue = run;
    await run;
  }

  private async flush(): Promise<void> {
    if (Object.keys(this.pending).length === 0) return;
    const patch = this.pending;
    this.pending = {};
    this.active = patch;
    const local: Settings = { ...this.base, ...patch };
    try {
      const version = useSettings.getState().version;
      const r = await this.docs.save("settings", "main", this.base, version, local);
      this.active = {};
      const adopted = this.adopt(r.kind === "saved" ? r.doc : r.server);
      if (!adopted) this.reapply();
      if (Object.keys(this.pending).length === 0) useSettings.setState({ syncStatus: "idle" });
    } catch (e) {
      // Keep the edit for the next save (it is still on screen).
      this.active = {};
      this.pending = { ...patch, ...this.pending };
      useSettings.setState({
        syncStatus: "error",
        lastError: e instanceof Error ? e.message : "save failed",
      });
      this.reapply();
    }
  }

  private adopt(doc: Doc<Settings>): boolean {
    // HTTP responses can complete out of order (including saves and conflict responses).
    // Compare the returned document, not the notification that originally requested it.
    if (doc.version < useSettings.getState().version) return false;
    const merged: Settings = { ...DEFAULT_SETTINGS, ...doc.body };
    // Documents from before display_time_zone: carry the old CT toggle over.
    const stored: Record<string, unknown> = doc.body;
    if (stored["display_time_zone"] === undefined && stored["timeDisplay"] === "ct")
      merged.display_time_zone = "exchange";
    this.base = merged;
    // Active intent remains visible while its outcome is unknown; newer local edits win over it.
    const shown: Settings = { ...merged, ...this.active, ...this.pending };
    useSettings.setState({ settings: shown, version: doc.version, loaded: true });
    this.apply(shown);
    return true;
  }

  /** A stale terminal response can retire active intent without changing the accepted base. */
  private reapply(): void {
    const shown: Settings = { ...this.base, ...this.active, ...this.pending };
    const current = useSettings.getState().settings;
    if (
      Object.keys(shown).length === Object.keys(current).length &&
      Object.keys(shown).every((key) => Object.hasOwn(current, key) && Object.is(shown[key], current[key]))
    )
      return;
    useSettings.setState({ settings: shown });
    this.apply(shown);
  }
}
